// =============================================================================
// Members list (§7). Search, plus the two filters that actually get used:
// payment pending and inactive.
//
// "Payment pending" is first because it is the most common reason a child is
// turned away at the desk, and the fix — record the fee from the receipt book —
// takes ten seconds once you can find who needs it.
//
// The list is a table, not a stack of cards. A card row put the name, the code,
// the phone and up to four pills on one line, so nothing lined up down the page
// and finding the one unpaid child meant reading every row. Columns line up.
// Sorting is client-side: listMembers already returns the whole branch.
// =============================================================================

import { el, mount } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { skeletonRows } from '../lib/loading.js';
import { liveSearch } from '../lib/live-search.js';
import { listMembers } from '../lib/api.js';

export function membersScreen(root) {
  let search = '';
  let filter = 'all';        // all | unpaid | inactive
  let rows = [];
  let loading = true;
  let sortKey = 'name';
  let sortDir = 1;           // 1 ascending, -1 descending

  const view = el('div', { class: 'screen' });
  mount(root, view);

  const input = el('input', {
    type: 'search', class: 'text-input', value: search,
    placeholder: T.searchPlaceholder, autocomplete: 'off', enterkeyhint: 'search',
    'aria-label': T.findMember,
  });

  // Typing searches. See lib/live-search.js for why the guard is needed.
  const live = liveSearch(input, async (term, stillCurrent) => {
    search = term;
    await load(stillCurrent);
  });

  async function load(stillCurrent = () => true) {
    loading = true; render();
    let next;
    try {
      next = await listMembers({
        search,
        status: filter === 'inactive' ? 'inactive' : undefined,
        unpaidOnly: filter === 'unpaid',
      });
    } catch (err) {
      if (!stillCurrent()) return;
      rows = [];
      mount(view, el('p', { class: 'error' }, err.message));
      loading = false;
      return;
    }
    if (!stillCurrent()) return;
    rows = next;
    loading = false; render();
  }

  function chip(key, label) {
    return el('button', {
      class: `chip ${filter === key ? 'chip-active' : ''}`,
      'aria-pressed': filter === key ? 'true' : 'false',
      onClick: () => { filter = key; load(); },
    }, label);
  }

  // What each column sorts BY, kept separate from what the cell shows: the
  // status column displays words but sorts by how much attention the row wants.
  const SORTS = {
    name:   (m) => (m.full_name || '').toLowerCase(),
    code:   (m) => (m.member_code || '').toLowerCase(),
    phone:  (m) => (m.guardian_phone || ''),
    books:  (m) => m.active_loans || 0,
    status: (m) => (m.overdue_loans > 0 ? 0 : !m.has_paid ? 1 : m.status === 'inactive' ? 2 : 3),
  };

  function sortedRows() {
    const pick = SORTS[sortKey] || SORTS.name;
    return [...rows].sort((a, b) => {
      const x = pick(a), y = pick(b);
      if (x === '' || x === null || x === undefined) return (y === '' || y == null) ? 0 : 1;
      if (y === '' || y === null || y === undefined) return -1;
      if (x < y) return -1 * sortDir;
      if (x > y) return 1 * sortDir;
      return (a.full_name || '').localeCompare(b.full_name || '');
    });
  }

  function sortBy(key) {
    if (sortKey === key) sortDir = -sortDir;
    else { sortKey = key; sortDir = 1; }
    render();
  }

  function sortableTh(key, label, extra = '') {
    const active = sortKey === key;
    return el('th', {
      class: `th-sort ${extra} ${active ? 'th-sort-active' : ''}`,
      tabindex: '0', role: 'button',
      'aria-sort': active ? (sortDir === 1 ? 'ascending' : 'descending') : 'none',
      onClick: () => sortBy(key),
      onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sortBy(key); } },
    }, label, el('span', { class: 'th-arrow' }, active ? (sortDir === 1 ? ' ▲' : ' ▼') : ''));
  }

  function render() {
    mount(view,
      el('div', { class: 'screen-head' },
        el('h1', { class: 'screen-title' }, T.membersTitle),
        el('a', { class: 'btn btn-primary', href: '#/members/new' }, T.addMember),
      ),
      // Enter still submits — it just runs the pending search now instead of
      // being the only way to run one.
      el('form', {
        class: 'search-row',
        onSubmit: (e) => { e.preventDefault(); live.now(); },
      }, input, el('button', { class: 'btn', type: 'submit' }, T.search)),
      el('div', { class: 'chips' },
        chip('all', T.filterAll),
        chip('unpaid', T.filterUnpaid),
        chip('inactive', T.filterInactive),
      ),
      loading
        // A skeleton the shape of the table, so the page does not collapse and
        // jump on every keystroke.
        ? el('div', { role: 'status', 'aria-live': 'polite', 'aria-label': T.loading },
            skeletonRows(6, 5))
        : rows.length === 0
          ? el('p', { class: 'empty' }, T.noMembers)
          : table(),
    );
  }

  function table() {
    return el('div', { class: 'table-wrap' },
      el('table', { class: 'table' },
        el('thead', {},
          el('tr', {},
            sortableTh('name', T.colMemberName),
            sortableTh('code', T.colCode, 'col-code'),
            sortableTh('phone', T.colPhone, 'col-phone'),
            sortableTh('books', T.colBooksOut, 'num'),
            sortableTh('status', T.colStatus),
          )),
        el('tbody', {}, ...sortedRows().map(row)),
      ));
  }

  function row(m) {
    return el('tr', { class: 'row-clickable', onClick: () => { location.hash = `#/members/${m.member_id}`; } },
      el('td', {},
        el('a', { class: 'cell-strong', href: `#/members/${m.member_id}` }, m.full_name)),
      el('td', { class: 'cell-copy-code col-code' }, m.member_code || '—'),
      // Tap-to-call: the overdue chase happens from a phone.
      el('td', { class: 'cell-muted cell-nowrap col-phone' },
        m.guardian_phone
          ? el('a', { href: `tel:${String(m.guardian_phone).replace(/\s/g, '')}` }, m.guardian_phone)
          : '—'),
      el('td', { class: 'num cell-muted' }, m.active_loans > 0 ? String(m.active_loans) : '—'),
      el('td', { class: 'cell-pills' },
        m.overdue_loans > 0 && el('span', { class: 'pill pill-danger' }, T.hasOverdue),
        !m.has_paid && el('span', { class: 'pill pill-warn' }, T.paymentPending),
        m.status === 'inactive' && el('span', { class: 'pill' }, T.inactive),
      ),
    );
  }

  load();
  return { destroy: () => live.stop() };
}
